import r2wc from '@r2wc/react-to-web-component';
import { useEffect, useMemo, useState } from 'react';
import '../../../styles/tailwind.css';

export interface CustomerMode {
  value: ModeValues;
  label: string;
  shortLabel: string;
  products: PricingProduct[];
}

export type TenantVariant = 0 | 1 | 5 | 10;

export interface PricingProduct {
  name: string;
  isAgency: boolean;
  isDpo: boolean;
  value: string;
  description: string;
  free?: boolean;
  price?: Price;
  tenantPrice?: Record<TenantVariant, Price>;
  featureTitle: string;
  features?: string[];
  pricingLink: {
    month: string;
    year: string;
  };
  withTest: boolean;
  preferred: boolean;
  selectableProducts?: TicketProduct[];
}

export interface TicketProduct {
  label: string;
  value: TopicValues;
  description: string;
  features: string[];
  price?: Price;
}

interface Price {
  month: number;
  year: number;
  discount: number;
}

interface IProps {
  queryoption?: string | null;
  user?: User;
  principalCompany?: BasicCompany;
  selectedProductValue?: TopicValues;
  locale?: string;
  querymode?: string | null;
  onQueryModeChange?(queryMode: string): void;
  inApp?: boolean;
  wixAppInstanceId?: string;
  wixPrice?: WixPrice;
  onUpgrade?(props: UpgradeProps): void;
  upgradeDisabledReason?: string;
}

import { ModeValues, TopicValues, UpgradeProps } from '@app/routes/Pricing/Pricing';
import { BasicCompany } from '@dieterApi/company/useCompanyQuery';
import { User } from '@dieterApi/user/useUserQuery';
import { WixPrice } from '@dieterApi/wix/useWixPriceQuery';
import { CelloNotification } from './CelloNotification';
import { FeatureTable } from './FeatureTable';
import { ProductCardV2 } from './ProductCardV2';
import { useLocalizedTranslation } from './Translation';

export function PricingTable({
  queryoption,
  user,
  principalCompany,
  selectedProductValue = 'dse',
  locale = 'de',
  querymode,
  onQueryModeChange,
  inApp = false,
  wixAppInstanceId,
  wixPrice,
  onUpgrade,
  upgradeDisabledReason,
}: IProps) {
  const { t: tl } = useLocalizedTranslation(locale);

  const preferredMode = principalCompany?.isAgency ? 'agencies' : principalCompany?.isDpo ? 'dpos' : 'companies';
  const [mode, setMode] = useState<ModeValues>((querymode || preferredMode || 'companies') as ModeValues);
  const [selectedProduct, setSelectedProduct] = useState<TicketProduct | undefined>(undefined);

  const [selectedTenants, setSelectedTenants] = useState<TenantVariant>(0);
  const userEmail = user?.email;
  // build checkout query params
  const emailParam = userEmail ? `&email=${userEmail}` : '';
  const localeParam = locale ? `&locale=${locale}` : '';
  const tenantParam = selectedTenants ? `&tenants=${selectedTenants}` : '';
  const ticketTopicParam = selectedProduct?.value ? `&topic=${selectedProduct?.value}` : '';
  const isWixUser = !!wixAppInstanceId;
  const wixParam = isWixUser ? `&wix_id=${wixAppInstanceId}` : '';
  const stripeCustomerIdParam = principalCompany?.stripeCustomerId
    ? `&stripe_customer_id=${principalCompany?.stripeCustomerId}`
    : '';
  const forCompanyParam =
    user?.company.isPrincipal === false && user?.company.id ? `&forcompany=${user?.company.id}` : '';

  // Determine the correct app URL based on the current environment
  const getCheckoutUrl = () => {
    const hostname = window.location.hostname;

    // Development/localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return window.location.origin;
    }

    // Staging environment
    if (hostname.includes('stage')) {
      return 'https://stage.dieter-datenschutz.de';
    }

    // Production (default)
    return 'https://app.dieter-datenschutz.de';
  };

  const checkoutUrl = getCheckoutUrl();

  const ticketProduct = {
    name: tl('ticket.name'),
    value: 'ticket',
    isAgency: false,
    isDpo: false,
    description: tl('ticket.description'),
    featureTitle: tl('ticket.featureTitle'),
    preferred: false,
    withTest: false,
    pricingLink: {
      month:
        `${checkoutUrl}/api/checkout?product=ticket&trial=false&interval=month` +
        emailParam +
        localeParam +
        ticketTopicParam +
        wixParam +
        stripeCustomerIdParam +
        forCompanyParam,
      year:
        `${checkoutUrl}/api/checkout?product=ticket&trial=false&interval=year` +
        emailParam +
        localeParam +
        ticketTopicParam +
        wixParam +
        stripeCustomerIdParam +
        forCompanyParam,
    },
    selectableProducts: [
      {
        label: tl('ticket.dse.label'),
        value: 'dse' as TopicValues,
        description: tl('ticket.dse.description'),
        features: [
          tl('ticket.dse.f1'),
          tl('ticket.dse.f2'),
          tl('ticket.dse.f3'),
          tl('ticket.dse.f4'),
          tl('ticket.dse.f5'),
          tl('ticket.dse.f6'),
          tl('ticket.dse.f7'),
        ],
        price: {
          month: isWixUser ? wixPrice?.monthly || 9 : 9,
          year: isWixUser ? wixPrice?.yearly || 7 : 7,
          discount: 22,
        },
      },

      ...(!isWixUser
        ? ([
            {
              label: tl('ticket.toms.label'),
              value: 'toms' as TopicValues,
              description: tl('ticket.toms.description'),
              features: [tl('ticket.toms.f1'), tl('ticket.toms.f2'), tl('ticket.toms.f3')],
              price: {
                month: 19,
                year: 15,
                discount: 22,
              },
            },
            {
              label: tl('ticket.vvt.label'),
              value: 'vvt' as TopicValues,
              description: tl('ticket.vvt.description'),
              features: [tl('ticket.vvt.f1'), tl('ticket.vvt.f2'), tl('ticket.vvt.f3')],
              price: {
                month: 19,
                year: 15,
                discount: 22,
              },
            },
            {
              label: tl('ticket.avv.label'),
              value: 'avv' as TopicValues,
              description: tl('ticket.avv.description'),
              features: [
                tl('ticket.avv.f1'),
                tl('ticket.avv.f2'),
                tl('ticket.avv.f3'),
                tl('ticket.avv.f4'),
                tl('ticket.avv.f5'),
              ],
              price: {
                month: 19,
                year: 15,
                discount: 22,
              },
            },
          ] as TicketProduct[])
        : []),
    ],
  };

  const basicProduct = {
    name: 'Basic',
    value: 'basic',
    isAgency: false,
    isDpo: false,
    description: tl('basic.description'),
    price: {
      month: 59,
      year: 29,
      discount: 50,
    },
    featureTitle: tl('basic.featureTitle'),
    features: [
      tl('basic.f1'),
      tl('basic.f2'),
      // tl('basic.f4'),
      tl('basic.f5'),
      tl('basic.f7'),
      tl('basic.f8'),
      tl('basic.f9'),
      tl('basic.f10'),
      tl('basic.f11'),
      tl('basic.f12'),
      tl('basic.f13'),
      tl('basic.f14'),
      tl('basic.f15'),
      tl('basic.f16'),
      tl('basic.f6'),
      tl('basic.f3'),
      tl('basic.f18'),
    ],
    preferred: true,
    withTest: true,
    pricingLink: {
      month:
        `${checkoutUrl}/api/checkout?product=basic&trial=true&interval=month` +
        emailParam +
        localeParam +
        stripeCustomerIdParam +
        forCompanyParam,
      year:
        `${checkoutUrl}/api/checkout?product=basic&trial=true&interval=year` +
        emailParam +
        localeParam +
        stripeCustomerIdParam +
        forCompanyParam,
    },
  };

  const premiumProduct = {
    name: 'Premium',
    value: 'premium',
    isAgency: false,
    isDpo: false,
    description: tl('premium.description'),
    price: {
      month: 99,
      year: 79,
      discount: 20,
    },
    featureTitle: tl('premium.featureTitle'),
    features: [
      tl('premium.f1'),
      tl('premium.f2'),
      tl('premium.f3'),
      tl('premium.f4'),
      tl('premium.f8'),
      tl('premium.f5'),
      tl('premium.f6'),
      tl('premium.f7'),
    ],
    preferred: false,
    withTest: true,
    pricingLink: {
      month:
        `${checkoutUrl}/api/checkout?product=premium&trial=true&interval=month` +
        emailParam +
        localeParam +
        stripeCustomerIdParam +
        forCompanyParam,
      year:
        `${checkoutUrl}/api/checkout?product=premium&trial=true&interval=year` +
        emailParam +
        localeParam +
        stripeCustomerIdParam +
        forCompanyParam,
    },
  };

  const premiumplusProduct = {
    name: 'Premium +',
    value: 'premiumplus',
    isAgency: false,
    isDpo: false,
    description: tl('premiumplus.description'),
    price: {
      month: 199,
      year: 149,
      discount: 25,
    },
    featureTitle: tl('premiumplus.featureTitle'),
    features: [
      tl('premiumplus.f1'),
      tl('premiumplus.f2'),
      tl('premiumplus.f3'),
      tl('premiumplus.f4'),
      // tl('premiumplus.f5'),
    ],
    preferred: false,
    withTest: true,
    pricingLink: {
      month:
        `${checkoutUrl}/api/checkout?product=premiumplus&trial=true&interval=month` +
        emailParam +
        localeParam +
        stripeCustomerIdParam +
        forCompanyParam,
      year:
        `${checkoutUrl}/api/checkout?product=premiumplus&trial=true&interval=year` +
        emailParam +
        localeParam +
        stripeCustomerIdParam +
        forCompanyParam,
    },
  };

  const customerModes: CustomerMode[] = [
    {
      value: 'companies',
      label: tl('customer_mode.companies'),
      shortLabel: tl('customer_mode_short.companies'),
      products: [ticketProduct, basicProduct, premiumProduct, premiumplusProduct],
    },
    {
      value: 'agencies',
      label: tl('customer_mode.agencies'),
      shortLabel: tl('customer_mode_short.agencies'),
      products: [
        {
          name: 'Agency Basic',
          isAgency: true,
          isDpo: false,
          value: 'basic',
          description: tl('agency_basic.description'),
          price: {
            month: 119,
            year: 69,
            discount: 42,
          },
          featureTitle: tl('agency_basic.featureTitle'),
          features: [
            tl('agency_basic.f1'),
            tl('agency_basic.f2'),
            // tl('agency_basic.f3'),
            tl('agency_basic.f4'),
            // tl('agency_basic.f5'),
            tl('agency_basic.f6'),
            tl('agency_basic.f7'),
          ],
          preferred: true,
          withTest: true,
          pricingLink: {
            month:
              `${checkoutUrl}/api/checkout?product=basic&trial=true&interval=month&mode=agencies` +
              emailParam +
              localeParam +
              stripeCustomerIdParam +
              forCompanyParam,
            year:
              `${checkoutUrl}/api/checkout?product=basic&trial=true&interval=year&mode=agencies` +
              emailParam +
              localeParam +
              stripeCustomerIdParam +
              forCompanyParam,
          },
        },
        {
          name: 'Agency Premium',
          value: 'premium',
          isAgency: true,
          isDpo: false,
          description: tl('agency_premium.description'),
          price: {
            month: 169,
            year: 129,
            discount: 25,
          },
          featureTitle: tl('agency_premium.featureTitle'),
          features: [
            tl('agency_premium.f1'),
            tl('agency_premium.f2'),
            tl('agency_premium.f3'),
            tl('agency_premium.f4'),
          ],
          preferred: false,
          withTest: true,
          pricingLink: {
            month:
              `${checkoutUrl}/api/checkout?product=premium&trial=true&interval=month&mode=agencies` +
              emailParam +
              localeParam +
              stripeCustomerIdParam +
              forCompanyParam,
            year:
              `${checkoutUrl}/api/checkout?product=premium&trial=true&interval=year&mode=agencies` +
              emailParam +
              localeParam +
              stripeCustomerIdParam +
              forCompanyParam,
          },
        },
      ],
    },
    {
      value: 'dpos',
      label: tl('customer_mode.dpos'),
      shortLabel: tl('customer_mode_short.dpos'),
      products: [
        {
          name: 'DSB',
          isAgency: false,
          isDpo: true,
          value: 'basic',
          description: tl('dpo_basic.description'),
          price: {
            month: 119,
            year: 69,
            discount: 42,
          },
          tenantPrice: {
            0: {
              month: 0,
              year: 0,
              discount: 0,
            },
            1: {
              month: 59,
              year: 29,
              discount: 50,
            },
            5: {
              month: 56,
              year: 27.5,
              discount: 50,
            },
            10: {
              month: 51,
              year: 25,
              discount: 50,
            },
          },
          featureTitle: tl('dpo_basic.featureTitle'),
          features: [
            tl('dpo_basic.f1'),
            tl('dpo_basic.f2'),
            tl('dpo_basic.f3'),
            tl('dpo_basic.f4'),
            tl('dpo_basic.f5'),
            tl('dpo_basic.f6'),
            tl('dpo_basic.f7'),
          ],
          preferred: true,
          withTest: true,
          pricingLink: {
            month:
              `${checkoutUrl}/api/checkout?product=basic&trial=true&interval=month&mode=dpos` +
              emailParam +
              localeParam +
              tenantParam +
              forCompanyParam,
            year:
              `${checkoutUrl}/api/checkout?product=basic&trial=true&interval=year&mode=dpos` +
              emailParam +
              localeParam +
              tenantParam +
              forCompanyParam,
          },
        },
      ],
    },
  ];

  const currentCustomerMode = useMemo(() => customerModes.findIndex(({ value }) => value === mode), [mode, locale]);

  const products = customerModes[currentCustomerMode].products as PricingProduct[];

  useEffect(() => {
    if (mode === 'companies') {
      setSelectedProduct(
        products
          .find((product) => product.value === 'ticket')
          ?.selectableProducts?.find((opt) => opt.value === (selectedProductValue || 'dse'))
      );
    }
  }, [selectedProductValue, mode, locale]);

  useEffect(() => {
    if (!principalCompany?.celloReferralCode) return;
  }, [principalCompany?.celloReferralCode]);

  const [isActive, setIsActive] = useState(true);

  const handleModeChange = (newMode: ModeValues) => {
    setMode(newMode);
    onQueryModeChange?.(newMode);
  };

  // Grid class varies by mode
  const gridClass =
    mode === 'companies'
      ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 xl:gap-8 items-stretch mx-auto'
      : mode === 'agencies'
        ? 'grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto items-stretch'
        : 'grid gap-6 max-w-md mx-auto';

  return (
    <div className="2xl:container mx-auto w-full text-left">
      {/* Tab Switcher */}
      {!isWixUser && (
        <div className="flex flex-col items-center gap-6 mb-10 md:mb-12">
          <div className="flex flex-wrap justify-center gap-2">
            {customerModes.map(({ value: modeValue, label, shortLabel }) => (
              <button
                key={modeValue}
                type="button"
                onClick={() => handleModeChange(modeValue)}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors border ${
                  mode === modeValue
                    ? 'bg-dieter-purple text-white border-dieter-purple'
                    : 'bg-white text-dieter-black border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span className="hidden md:inline">{label}</span>
                <span className="md:hidden">{shortLabel}</span>
              </button>
            ))}
          </div>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-3">
            <span className={`text-sm font-medium ${!isActive ? 'text-dieter-black' : 'text-gray-400'}`}>
              {tl('common.monthly_billing')}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={isActive}
              onClick={() => setIsActive((v) => !v)}
              className={`inline-flex h-7 w-12 shrink-0 rounded-full border-2 border-gray-300 p-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-dieter-purple ${
                isActive ? 'justify-end bg-pricing-purple-bright' : 'justify-start bg-gray-200'
              }`}
            >
              <span className="block h-5 w-5 shrink-0 rounded-full bg-white shadow transition-all duration-200" />
            </button>
            <span className={`text-sm font-medium ${isActive ? 'text-dieter-black' : 'text-gray-400'}`}>
              {tl('common.yearly_billing')}
            </span>
          </div>
        </div>
      )}

      <CelloNotification principalCompany={principalCompany} />

      {/* Product Cards Grid */}
      <div className={gridClass}>
        {products
          .filter((p) => !isWixUser || p.value === 'ticket')
          .map((product) => (
            <ProductCardV2
              key={product.name}
              product={product}
              user={user}
              principalCompany={principalCompany}
              mode={mode}
              interval={isActive ? 'year' : 'month'}
              showButton={!product.free || !inApp}
              queryOption={queryoption}
              selectedProduct={selectedProduct}
              onProductSelect={setSelectedProduct}
              selectedTenants={selectedTenants}
              onTenantSelect={setSelectedTenants}
              isUpgradeHighlight={selectedProduct?.value === selectedProductValue && product.value === 'ticket'}
              locale={locale}
              isWixUser={isWixUser}
              onUpgrade={onUpgrade}
              upgradeDisabledReason={upgradeDisabledReason}
            />
          ))}
      </div>

      <FeatureTable locale={locale} />
    </div>
  );
}

const PricingTableComponent = r2wc(PricingTable as any, {
  props: { locale: 'string', querymode: 'string', queryoption: 'string' },
});

if (!customElements.get('pricing-table')) {
  customElements.define('pricing-table', PricingTableComponent);
}
